import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@/hooks/useTheme';
import { projectService } from '@/services/projectService';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { ProjectSearchSelect } from '@/components/ProjectSearchSelect';
import { SuperAdminPageHelp } from '@/components/SuperAdmin/SuperAdminPageHelp';
import { useAuthStore } from '@/store/authStore';

type TerminalLevel = 'info' | 'success' | 'warning' | 'error' | 'muted';
type TerminalScope =
  | 'INIT'
  | 'CONNECT'
  | 'BOX'
  | 'DEVICE'
  | 'DEVICE_TYPE'
  | 'ATTRIBUTE'
  | 'ATTRIBUTE_TYPE'
  | 'SUMMARY'
  | 'FATAL'
  | 'UNKNOWN';

type TerminalLine = {
  id: string;
  ts: number;
  level: TerminalLevel;
  scope: TerminalScope;
  message: string;
};

export function SuperImportEquipmentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const [selectedProjectIri, setSelectedProjectIri] = useState<string>('');
  const [terminalOutput, setTerminalOutput] = useState<TerminalLine[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [boxNamePrefix, setBoxNamePrefix] = useState<string>('');
  const terminalRef = useRef<HTMLDivElement>(null);

  const platformProjectId = useMemo(() => {
    if (!selectedProjectIri) return null;
    const n = parseInt(selectedProjectIri.replace('/api/projects/', ''), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedProjectIri]);

  // Pre-fill project from ?projectId=X when coming from ProjectsPage
  useEffect(() => {
    const projectId = searchParams.get('projectId');
    if (projectId && !selectedProjectIri) {
      const id = parseInt(projectId, 10);
      if (Number.isFinite(id) && id > 0) {
        setSelectedProjectIri(`/api/projects/${id}`);
      }
    }
  }, [searchParams, selectedProjectIri]);

  const { data: project } = useQuery({
    queryKey: ['project', platformProjectId],
    queryFn: () => projectService.getProject(platformProjectId!),
    enabled: !!platformProjectId,
  });

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalOutput]);

  const parseTerminalLine = (raw: string): TerminalLine => {
    const ts = Date.now();
    const msg = raw.trimEnd();
    const id = `${ts}-${Math.random().toString(16).slice(2)}`;

    const levelMatch = msg.match(/^\[(INFO|OK|WARN|ERROR)\]/);
    const scopeMatch = msg.match(/^\[(?:INFO|OK|WARN|ERROR)\]\[([A-Z_]+)\]/);

    const level: TerminalLevel = (() => {
      const lv = levelMatch?.[1] ?? null;
      if (lv === 'OK') return 'success';
      if (lv === 'WARN') return 'warning';
      if (lv === 'ERROR') return 'error';
      if (lv === 'INFO') return 'info';
      return 'muted';
    })();

    const scope: TerminalScope = (() => {
      const s = scopeMatch?.[1] ?? null;
      const allowed: TerminalScope[] = [
        'INIT',
        'CONNECT',
        'BOX',
        'DEVICE',
        'DEVICE_TYPE',
        'ATTRIBUTE',
        'ATTRIBUTE_TYPE',
        'SUMMARY',
        'FATAL',
      ];
      if (s && (allowed as string[]).includes(s)) return s as TerminalScope;
      return 'UNKNOWN';
    })();

    const clean = msg.replace(/^\[(INFO|OK|WARN|ERROR)\](\[[A-Z_]+\]){0,3}\s*/g, '');

    return { id, ts, level, scope, message: clean };
  };

  const appendTerminalLines = (rawMessage: string, fallbackLevel: TerminalLevel = 'info') => {
    const lines = rawMessage.split(/\r?\n/).filter((l) => l.trim() !== '');
    if (lines.length === 0) return;

    setTerminalOutput((prev) => {
      const next = [...prev];
      for (const l of lines) {
        const parsed = parseTerminalLine(l);
        next.push(
          parsed.scope === 'UNKNOWN' && parsed.level === 'muted'
            ? { ...parsed, level: fallbackLevel }
            : parsed
        );
      }
      return next;
    });
  };

  const scopeIndent = (scope: TerminalScope): string => {
    if (scope === 'DEVICE' || scope === 'DEVICE_TYPE') return 'pl-6';
    if (scope === 'ATTRIBUTE' || scope === 'ATTRIBUTE_TYPE') return 'pl-10';
    if (scope === 'SUMMARY') return 'pl-2';
    return 'pl-2';
  };

  const lineColor = (level: TerminalLevel): string => {
    switch (level) {
      case 'error':
        return 'text-red-400';
      case 'warning':
        return 'text-yellow-400';
      case 'success':
        return 'text-green-400';
      case 'info':
        return 'text-blue-400';
      default:
        return 'text-gray-300';
    }
  };

  const scopeBadge = (scope: TerminalScope): { label: string; cls: string } => {
    const map: Record<TerminalScope, { label: string; cls: string }> = {
      INIT: { label: 'INIT', cls: 'bg-slate-500/10 text-slate-300 border border-slate-500/20' },
      CONNECT: { label: 'CONNECT', cls: 'bg-blue-500/10 text-blue-300 border border-blue-500/20' },
      BOX: { label: 'BOX', cls: 'bg-purple-500/10 text-purple-300 border border-purple-500/20' },
      DEVICE: { label: 'DEVICE', cls: 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20' },
      DEVICE_TYPE: { label: 'TYPE', cls: 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20' },
      ATTRIBUTE: { label: 'ATTR', cls: 'bg-amber-500/10 text-amber-300 border border-amber-500/20' },
      ATTRIBUTE_TYPE: { label: 'TYPE', cls: 'bg-amber-500/10 text-amber-300 border border-amber-500/20' },
      SUMMARY: { label: 'SUMMARY', cls: 'bg-slate-500/10 text-slate-300 border border-slate-500/20' },
      FATAL: { label: 'FATAL', cls: 'bg-red-500/10 text-red-300 border border-red-500/20' },
      UNKNOWN: { label: 'LOG', cls: 'bg-slate-500/10 text-slate-300 border border-slate-500/20' },
    };
    return map[scope];
  };

  const addTerminalLine = (message: string, type: TerminalLevel = 'info') => {
    appendTerminalLines(`[${type.toUpperCase()}][INIT] ${message}`, type);
  };

  const canStart = !isImporting && !!platformProjectId;

  const handleImport = async () => {
    if (!platformProjectId) {
      addTerminalLine('Veuillez sélectionner un projet', 'error');
      return;
    }

    setIsImporting(true);
    setTerminalOutput([]);
    addTerminalLine('Démarrage de l\'import...', 'info');

    try {
      await projectService.importEquipmentsStream(
        platformProjectId,
        undefined,
        boxNamePrefix,
        (data) => {
          if (data.type === 'start') {
            appendTerminalLines(`[INFO][INIT] ${data.message}`, 'info');
          } else if (data.type === 'output') {
            const cleanMessage = data.message.replace(/\x1b\[[0-9;]*m/g, '');
            appendTerminalLines(cleanMessage, 'info');
          } else if (data.type === 'complete') {
            if (data.status === 'success') {
              appendTerminalLines(`[OK][SUMMARY] ${data.message}`, 'success');
            } else {
              appendTerminalLines(`[ERROR][SUMMARY] ${data.message}`, 'error');
            }
            setIsImporting(false);
          } else if (data.type === 'error') {
            appendTerminalLines(`[ERROR][FATAL] ${data.message}`, 'error');
            setIsImporting(false);
          }
        },
        (error) => {
          addTerminalLine(`Erreur: ${error.message}`, 'error');
          setIsImporting(false);
        }
      );
    } catch (error: unknown) {
      addTerminalLine(`Erreur: ${error instanceof Error ? error.message : String(error)}`, 'error');
      setIsImporting(false);
    }
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Super utilisateur</span> / Import Équipements
          </div>
          <div className="flex items-center gap-4">
            <SuperAdminPageHelp path="/admin/super-import-equipments" />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              <span className="material-icons-outlined">{isDark ? 'light_mode' : 'dark_mode'}</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <span className="material-icons-outlined text-lg">person</span>
              <span>{user?.fullName || 'Admin'}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Import des Équipements</h1>

            <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-6 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                  Projet
                </label>
                <ProjectSearchSelect
                  value={selectedProjectIri}
                  onChange={(iri) => {
                    setSelectedProjectIri(iri);
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      const id = iri ? iri.replace('/api/projects/', '') : '';
                      if (id) next.set('projectId', id);
                      else next.delete('projectId');
                      return next;
                    });
                  }}
                  disabled={isImporting}
                />
              </div>

              <div className="mt-4 flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                    Filtre (préfixes nom de box, séparés par virgule) – optionnel
                  </label>
                  <input
                    type="text"
                    value={boxNamePrefix}
                    onChange={(e) => setBoxNamePrefix(e.target.value)}
                    placeholder='Ex: "30, 140"'
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-[#0B1116] border border-slate-200 dark:border-[#1E293B] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white placeholder-slate-400"
                    disabled={isImporting}
                  />
                </div>
                <button
                  onClick={handleImport}
                  disabled={!canStart}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className={`material-icons-outlined text-lg mr-2 ${isImporting ? 'animate-spin' : ''}`}>
                    {isImporting ? 'sync' : 'play_arrow'}
                  </span>
                  {isImporting ? 'Import en cours...' : 'Lancer l\'Importation'}
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-[500px] flex flex-col bg-[#0a0a0a] rounded-xl border border-gray-800 shadow-2xl overflow-hidden relative group/terminal">
              <div className="flex items-center justify-between px-4 py-3 bg-[#1a1a1a] border-b border-[#333]">
                <div className="flex items-center gap-2">
                  <div className="flex gap-2 mr-4">
                    <div className="size-3 rounded-full bg-[#ff5f56]" />
                    <div className="size-3 rounded-full bg-[#ffbd2e]" />
                    <div className="size-3 rounded-full bg-[#27c93f]" />
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 rounded bg-[#000] border border-[#333]">
                    <span className="material-icons-outlined text-xs text-gray-500">lock</span>
                    <span className="text-xs text-gray-400 font-mono">admin@gtb-core:~</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="px-2 py-0.5 rounded text-[10px] font-mono bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    {isImporting ? 'RUNNING' : 'READY'}
                  </div>
                  <button
                    onClick={() => setTerminalOutput([])}
                    className="text-gray-600 text-sm cursor-pointer hover:text-white transition-colors"
                    title="Vider le terminal"
                  >
                    <span className="material-icons-outlined text-sm">close</span>
                  </button>
                </div>
              </div>

              <div
                ref={terminalRef}
                className="flex-1 p-6 font-mono text-sm md:text-base overflow-y-auto terminal-scroll relative"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#333 #111' }}
              >
                <div className="flex flex-col gap-1">
                  {terminalOutput.length === 0 ? (
                    <div className="text-gray-500 mb-4">
                      {project ? `Terminal prêt. Projet: ${project.title}` : 'Sélectionnez un projet et cliquez sur "Lancer l\'Importation".'}
                    </div>
                  ) : (
                    terminalOutput.map((line) => {
                      const badge = scopeBadge(line.scope);
                      return (
                        <div key={line.id} className={`flex items-start gap-2 ${scopeIndent(line.scope)}`}>
                          <span
                            className={`mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono ${badge.cls}`}
                          >
                            {badge.label}
                          </span>
                          <span className={`${lineColor(line.level)} whitespace-pre-wrap break-words flex-1`}>
                            {line.message}
                          </span>
                        </div>
                      );
                    })
                  )}
                  {isImporting && (
                    <div className="flex items-center gap-2 mt-2 text-white">
                      <span className="text-primary">➜</span>
                      <span>Processing...</span>
                      <span className="w-2.5 h-5 bg-primary animate-pulse inline-block align-middle ml-1" />
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-[#111] border-t border-[#333] px-4 py-1.5 flex justify-between items-center text-[10px] md:text-xs font-mono text-gray-500">
                <div className="flex gap-4">
                  <span>LINES: {terminalOutput.length}</span>
                  <span>STATUS: {isImporting ? 'RUNNING' : 'IDLE'}</span>
                </div>
                <div className="flex gap-2 items-center">
                  {isImporting && (
                    <>
                      <div className="size-2 rounded-full bg-primary animate-pulse" />
                      <span className="text-primary">RUNNING</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
